import { FilterService } from 'primeng/api'
import { Table } from 'primeng/table'
import { ObjectUtils } from 'primeng/utils'
import { SearchService } from 'src/app/services/search.service'

/**
 * Hidden field added on every row before filtering. It holds all the searchable
 * fields of the row concatenated, which is what makes a search across several
 * fields ("prénom + nom de famille") possible: PrimeNG evaluates the filter
 * field by field, so a term spanning two fields never matches on its own.
 */
export const SEARCH_INDEX_FIELD = '__search'

/**
 * Custom PrimeNG match mode used by every search bar of the app. Unlike the
 * built-in `contains`, it splits the typed text into words and requires each
 * word to be found, in any order. Accents and case are ignored.
 */
export const SEARCH_MATCH_MODE = 'corderoSearch'

/**
 * Registers {@link SEARCH_MATCH_MODE} on PrimeNG's filter service. Must be
 * called once at application startup.
 */
export function registerSearchMatchMode(filterService: FilterService): void {
  filterService.register(SEARCH_MATCH_MODE, (value: unknown, filter: unknown): boolean => {
    const words = toSearchWords(filter)
    if (words.length === 0) return true
    if (value === null || value === undefined) return false

    const haystack = normalizeSearchText(value)
    return words.every(word => haystack.includes(word))
  })
}

/**
 * Applies the global search of a PrimeNG table. Uses the `globalFilterFields`
 * already declared on the table, but matches every word of the search text
 * across all of them at once.
 * @param table The table to filter
 * @param text The text typed by the user
 */
export function applyGlobalSearch(table: Table, text: string): void {
  if (!table) return

  const fields = getSearchableFields(table)
  buildSearchIndex(table, fields)
  table.globalFilterFields = [SEARCH_INDEX_FIELD, ...fields]
  table.filterGlobal(text ?? '', SEARCH_MATCH_MODE)
}

export function applySearchFilter({
  searchService,
  table,
  currentText,
  event,
  uniqueSearchKey,
}: ApplySearchFilterParams): string {
  const newText = event ? (event.target as HTMLInputElement).value : currentText

  if (uniqueSearchKey) searchService.setSearchText(uniqueSearchKey, newText)

  applyGlobalSearch(table, newText)

  return newText
}

interface ApplySearchFilterParams {
  searchService: SearchService
  table: Table
  currentText: string
  event?: Event
  uniqueSearchKey: string
}

/** Fields declared on the table, remembered so the index field never replaces them. */
const searchableFieldsByTable = new WeakMap<Table, string[]>()

function getSearchableFields(table: Table): string[] {
  const declared = ((table.globalFilterFields ?? []) as unknown[])
    .map(field => (typeof field === 'string' ? field : (field as { field?: string })?.field))
    .filter((field): field is string => !!field && field !== SEARCH_INDEX_FIELD)

  if (declared.length > 0) searchableFieldsByTable.set(table, declared)

  return searchableFieldsByTable.get(table) ?? []
}

function buildSearchIndex(table: Table, fields: string[]): void {
  if (fields.length === 0) return

  for (const row of table.value ?? []) {
    if (!row || typeof row !== 'object') continue

    const index = fields
      .map(field => ObjectUtils.resolveFieldData(row, field))
      .filter(value => value !== null && value !== undefined)
      .join(' ')

    // Non enumerable so the index never ends up in a spread, a JSON payload
    // sent back to the API or an exported report.
    Object.defineProperty(row, SEARCH_INDEX_FIELD, {
      value: index,
      enumerable: false,
      writable: true,
      configurable: true,
    })
  }
}

function toSearchWords(filter: unknown): string[] {
  if (filter === null || filter === undefined) return []

  return normalizeSearchText(filter).split(' ').filter(Boolean)
}

/** Lowercases, strips accents and collapses whitespace so "Éric" matches "eric". */
function normalizeSearchText(value: unknown): string {
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}
